/* eslint-disable react/prop-types */
/** @jsx jsx */
import React, { useContext, useRef, useState, useEffect } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { ThemeProvider, jsx } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';
import LocationSelectorFilter from '../LocationSelector';
import Checkbox from './CheckboxItem';
import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Text from '../../elements/Text';
import Button from '../../elements/Button';
import VisuallyHidden from '@reach/visually-hidden';
import { strFormat } from '../../../helpers/utils';

const AirlinesFilter = React.forwardRef((props, receivedRef) => {
  const {
    isMobile,
    footerContainer,
    buttonsContainer,
    resetButtonSettings,
    doneButtonSettings,
    separatorStyle,
    showSideMessage,
    countSelectedStyle,
    selectedStyle,
    navigation
  } = props;

  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);

  const alwaysEnabledDoneButton = doneButtonSettings.alwaysEnabledDoneButton;
  const ownRef = useRef();
  const ref = receivedRef || ownRef;
  const doneButtonRef = useRef();
  const flexibleFilterContainerRef = useRef();
  const locationSelectorRef = useRef();
  const { listItem, allItems } = props;
  const SPECIAL_IATA_CODE_ALL_ELEMENTS = -1;
  const ESC_KEY = 27;
  const ENTER_KEY = 13;
  const SPACE_KEY = 32;
  const TAB_KEY = 9;
  const ARROW_UP_KEY = 38;
  const ARROW_DOWN_KEY = 40;

  const ITEM_ALL_ELEMENTS = React.useMemo(() => {
    return {
      label: translate('cmp-filter-all-airlines-selected', 'All Airlines'),
      iataCode: SPECIAL_IATA_CODE_ALL_ELEMENTS
    };
  }, [translate, SPECIAL_IATA_CODE_ALL_ELEMENTS]);

  const isEqual = (options, selected) => {
    if (options === undefined || selected === undefined || options === null || selected === null) {
      return false;
    }
    if (options.length !== selected.length) {
      return false;
    }
    for (let index = 0; index < options.length; index++) {
      if (options[index].iataCode !== selected[index].iataCode) {
        return false;
      }
    }
    return true;
  };
  const airlinesOptions = React.useMemo(() => {
    return (Array.isArray(props.airlines.data) && props.airlines.data.length !== 0
      ? props.airlines.data.length > 1
        ? [].concat([ITEM_ALL_ELEMENTS], props.airlines.data)
        : props.airlines.data
      : []
    ).map((airlineItem) => {
      return {
        ...airlineItem,
        label: translate(airlineItem.label, airlineItem.defaultValue)
      };
    });
  }, [props.airlines.data, ITEM_ALL_ELEMENTS, translate]);

  const locationSelectorFilterRef = useRef();
  const [userInteraction, setUserInteraction] = useState(false);
  const [itemSelectedByKeyboard, setItemSelectedByKeyboard] = useState(null);
  const [count, setCount] = useState(0);
  const [selectedItems, setSelectedItems] = useState(() => {
    if (props.selectedAirlines === null) {
      props.onChange(undefined);
      return [];
    } else {
      return props.selectedAirlines !== undefined
        ? isEqual(airlinesOptions, props.selectedAirlines)
          ? airlinesOptions
          : props.selectedAirlines
        : [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  const labelToggleButton = translate('cmp-airline-filter-placeholder', 'Select airline');

  const [labelSelectedItems, setLabelSelectedItems] = useState({
    label: labelToggleButton,
    value: labelToggleButton
  });

  useEffect(() => {
    if (!props.airlines.isLoading && !userInteraction) {
      if (airlinesOptions.length > 0) {
        if (selectedItems.length === 0) {
          setSelectedItems(airlinesOptions);
        }
      } else {
        if (selectedItems.length !== 0) {
          setSelectedItems([]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airlinesOptions, selectedItems, userInteraction]);

  const isItem = (item, array) => {
    if (array !== undefined) {
      for (let index = 0; index < array.length; index++) {
        const element = array[index];
        if (element.iataCode === item.iataCode) {
          return index;
        }
      }
    }
    return -1;
  };

  const focusFilterButton = () => {
    if (locationSelectorRef) {
      locationSelectorRef?.current?.focusSelectorInput();
    }
  };

  useEffect(() => {
    // if (props.origin.value === undefined || props.destination.value === undefined) {
    setSelectedItems([]);
    props.clear();
    setUserInteraction(false);
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.origin.value, props.destination.value]);

  React.useEffect(() => {
    if (props.selectedAirlines) {
      let newlabel;
      if (props.selectedAirlines.length === 0) {
        newlabel = translate('cmp-airlines-filter-title', 'Airlines');
      } else if (props.selectedAirlines.length === 1) {
        newlabel = translate(
          props.selectedAirlines[0].label,
          props.selectedAirlines[0].defaultValue
        );
      } else if (props.selectedAirlines.length === airlinesOptions.length) {
        newlabel = translate('cmp-airlines-filter-all-items-selected', 'All Airlines');
      } else {
        newlabel = `${translate(
          'cmp-airlines-filter-multiple-items-selected',
          'Multiple Airlines'
        )} (${props.selectedAirlines.length})`;
      }
      setLabelSelectedItems({ label: newlabel, value: newlabel });
    } else {
      setLabelSelectedItems({
        label: translate('cmp-airline-filter-placeholder-no-available', 'No available airlines'),
        value: translate('cmp-airline-filter-placeholder-no-available', 'No available airlines')
      });
    }
  }, [props.selectedAirlines, airlinesOptions, translate]);

  useEffect(() => {
    if (props.selectedAirlines && isOpen) {
      setSelectedItems(props.selectedAirlines);
    }
  }, [isOpen, props.selectedAirlines]);

  useEffect(() => {
    if (selectedItems.length === 0) {
      new Promise((resolve) => setTimeout(resolve, 700)).then(() => setCount(0));
    }
  }, [selectedItems.length]);

  const selectItem = (itemMarked) => {
    setUserInteraction(true);
    if (selectedItems.length === 0) {
      if (itemMarked.iataCode === SPECIAL_IATA_CODE_ALL_ELEMENTS) {
        return airlinesOptions;
      } else {
        return [itemMarked];
      }
    } else {
      if (itemMarked !== undefined) {
        const index = isItem(itemMarked, selectedItems);
        if (index !== -1 && itemMarked.iataCode !== SPECIAL_IATA_CODE_ALL_ELEMENTS) {
          const optionsWhitoutAllItems = selectedItems.filter(
            (e) => e.iataCode !== SPECIAL_IATA_CODE_ALL_ELEMENTS
          );
          if (optionsWhitoutAllItems.length === 1) {
            return [];
          } else {
            let aux = [...selectedItems];
            aux.splice(index, 1);
            const index1 = isItem(ITEM_ALL_ELEMENTS, aux);
            if (index1 !== -1) {
              aux.splice(index1, 1);
            }
            return aux;
          }
        } else {
          if (itemMarked.iataCode === SPECIAL_IATA_CODE_ALL_ELEMENTS) {
            if (selectedItems.length !== airlinesOptions.length) {
              return airlinesOptions;
            } else {
              return [];
            }
          } else {
            let tmp = [...selectedItems];
            tmp.push(itemMarked);
            //if (props.faresDataAirline.length > 1) {
            if (tmp.length === airlinesOptions.length - 1) {
              return airlinesOptions;
            }
            //} else if (tmp.length === airlinesOptions.length - 1) {
            //return airlinesOptions;
            //}
            return tmp;
          }
        }
      }
    }
  };
  const onKeyUp = (e) => {
    if (e.keyCode === ESC_KEY) {
      locationSelectorRef.current?.onCloseMenu();
      if (props.setWasClear) {
        props.setWasClear(true);
      }
      focusFilterButton();
    }
  };
  const onKeyDown = (e) => {
    if (e.keyCode === TAB_KEY && e.shiftKey) {
      locationSelectorRef.current?.onCloseMenu();
    }
    if (e.keyCode === ARROW_UP_KEY || e.keyCode === ARROW_DOWN_KEY) {
      locationSelectorRef.current?.onOpenMenu();
    }
  };

  function renderItem(item, index) {
    const focusetItemValue =
      itemSelectedByKeyboard !== null && item.label === itemSelectedByKeyboard?.label;
    if (focusetItemValue) {
      setItemSelectedByKeyboard(null);
    }
    return (
      <>
        <Checkbox
          key={item.defaultValue}
          {...props}
          {...listItem}
          allItems={
            item.iataCode === SPECIAL_IATA_CODE_ALL_ELEMENTS
              ? { ...separatorStyle, ...allItems }
              : {}
          }
          onClickSideMessage={(items) => {
            if (!props.isMobile) {
              setSelectedItems([]);
              setItemSelectedByKeyboard(item);
            } else {
              setSelectedItems([items]);
            }
            setUserInteraction(true);
          }}
          showSideMessage={showSideMessage && item.iataCode !== SPECIAL_IATA_CODE_ALL_ELEMENTS}
          isMobile={props.isMobile}
          item={item}
          disabled={true}
          isChecked={isItem(item, selectedItems) !== -1}
          onChange={(isChecked, itemMarked) => {
            setCount(-1);
            // if (props.isMobile) {
            //   const newSelectedItems = selectItem(itemMarked);
            //   setSelectedItems(newSelectedItems);
            // }
            setTimeout(() => {
              setCount(
                selectedItems.find((x) => x.value === itemMarked.value) === undefined
                  ? selectedItems.length + 1
                  : selectedItems.length - 1
              );
            }, 1100);
          }}
          onKeyDown={(e) => {
            if (e.keyCode === ENTER_KEY || e.keyCode === SPACE_KEY) {
              e.preventDefault();
              const newSelectedItems = selectItem(item);
              setSelectedItems(newSelectedItems);
            }
          }}
          enableFocus
          setFocusOnItem={focusetItemValue}
          onKeyUp={onKeyUp}
        ></Checkbox>
      </>
    );
  }

  const renderFooter = () => (
    <Box display="flex" {...footerContainer}>
      <Text dataTestId="airlines-filter-counter" {...countSelectedStyle}>
        {selectedItems.length !== 0 && selectedItems[0].iataCode === -1
          ? selectedItems.length - 1
          : selectedItems.length}
      </Text>
      <Text {...selectedStyle}>{translate('cmp-airlines-info-options-selected', ' selected')}</Text>

      <Row {...buttonsContainer}>
        <Col>
          <Button
            color="light"
            {...resetButtonSettings}
            disabled={selectedItems.length === airlinesOptions.length}
            onClick={() => {
              setSelectedItems(airlinesOptions);
              doneButtonRef?.current?.focus();
            }}
            onKeyDown={(e) => {
              if (
                e.keyCode === ESC_KEY ||
                (!e.shiftKey && e.key === 'Tab' && selectedItems.length === 0)
              ) {
                e.preventDefault();
                locationSelectorRef.current?.onCloseMenu();
                if (props.setWasClear) {
                  props.setWasClear(true);
                }
                focusFilterButton();
              }
            }}
          >
            {translate('cmp-traveler-interests-reset-button', 'Reset')}
          </Button>
        </Col>
        <Col>
          <Button
            focusReferences={doneButtonRef}
            color="primary"
            {...doneButtonSettings}
            disabled={alwaysEnabledDoneButton ? false : selectedItems.length === 0}
            onClick={() => {
              props.onChange(selectedItems);
              locationSelectorRef.current?.onCloseMenu();

              if (flexibleFilterContainerRef.current?.save) {
                flexibleFilterContainerRef.current?.save();
              }
              if (props.setWasClear) {
                props.setWasClear(true);
              }
              if (props.navigation) {
                props.goBack();
                locationSelectorRef.current?.onCloseMenu();
              } else {
                focusFilterButton();
              }
            }}
            onKeyDown={(e) => {
              if ((!e.shiftKey && e.key === 'Tab') || e.keyCode === ESC_KEY) {
                e.preventDefault();
                locationSelectorRef.current?.onCloseMenu();
                if (props.setWasClear) {
                  props.setWasClear(true);
                }
                focusFilterButton();
              }
            }}
          >
            {translate('cmp-traveler-interests-done-button', 'Done')}
          </Button>
        </Col>
      </Row>
      <VisuallyHidden aria-live="polite">
        {count !== -1 && selectedItems.length !== 0
          ? strFormat(translate('cmp-traveler-info-options-selected', '{0} selected'), [
              selectedItems[0].iataCode === SPECIAL_IATA_CODE_ALL_ELEMENTS
                ? selectedItems.length - 1
                : selectedItems.length
            ])
          : ''}
      </VisuallyHidden>
      <VisuallyHidden aria-live="polite">
        {count === 0 ? translate('cmp-traveler-info-not-options-selected', 'No item selected') : ''}
      </VisuallyHidden>
    </Box>
  );

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        <LocationSelectorFilter
          required={props.isAvailableFilter && !props.disabled}
          labelKey="airlines"
          selectedItems={selectedItems}
          multipleSelection={true}
          multipleSelectionGroupLabel={false}
          options={airlinesOptions}
          ref={locationSelectorRef}
          dataTestId="locationSelector-airlines"
          placeholder={
            airlinesOptions.length === 0 && !props.disabled
              ? translate('cmp-airline-filter-placeholder-no-available', 'No available airlines')
              : translate('cmp-airline-filter-placeholder', 'Select airlines')
          }
          inputFocusReferences={ref ?? locationSelectorFilterRef}
          itemRenderer={renderItem}
          footerElement={!isMobile && renderFooter}
          openOnClick
          slowCloseAction={true}
          enableVirtualList={false}
          useInput={false}
          isDirty={props.isDirty}
          customEvents
          {...props}
          valueField={'defaultValue'}
          toggleButtonLabels={
            props.disabled
              ? { label: labelToggleButton, defaultValue: labelToggleButton }
              : labelSelectedItems
          }
          showToggleButtonLabels={Boolean(props.selectedAirlines)}
          onChange={(item) => {
            if (item !== undefined) {
              if (selectedItems.length === 0 && item.iataCode !== SPECIAL_IATA_CODE_ALL_ELEMENTS) {
                setSelectedItems([item]);
                setItemSelectedByKeyboard(item);
                locationSelectorRef.current?.update();
                return;
              }
              const newSelectedItems = selectItem(item);
              setSelectedItems(newSelectedItems);
              setItemSelectedByKeyboard(item);
              locationSelectorRef.current?.update();
            }
          }}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          notifyState={(e) => {
            setIsOpen(e);
          }}
          defaultIsOpen={navigation}
          onBlur={() => {
            if (props.isDirty) {
              props.setWasClear(false);
            }
          }}
        />
        {props.navigation && <>{renderFooter()}</>}
      </ThemeProvider>
    </EmotionCache>
  );
});

AirlinesFilter.defaultProps = {
  icon: 'flight-type',
  showSelectorMenuArrow: false,
  openOnFocus: true,
  showLabel: true,
  showLabelInside: false,
  clearable: false,
  selectMode: true,
  showCode: false,
  showArrowsIfClearable: false,
  faresDataAirline: [],
  menuItem: {
    shadow: '4',
    paddingHeight: '0',
    rounded: '0',
    border: true
  },
  inputItem: {
    shadow: '@theme:input.shadow',
    rounded: '@theme:input.rounded'
  },
  listItem: {
    hoverBgColor: 'primary',
    hoverTextColor: 'white',
    paddingLeft: '4',
    paddingRight: '1',
    paddingHeight: '2'
  },
  groupItem: {
    hoverBgColor: 'primary',
    hoverTextColor: 'white',
    paddingWidth: '1',
    paddingHeight: '2'
  },
  noResultItem: {
    paddingWidth: '1',
    paddingHeight: '2'
  }
};

export default AirlinesFilter;
